import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { queryAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { QueryResponse, ChartSuggestion, InsightResult } from "../types";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import ChatMessage from "../components/ChatMessage";
import UpgradePrompt from "../components/UpgradePrompt";
import LoadingMessages from "../components/LoadingMessages";
import { cn } from "../lib/utils";
import { Send, AlertCircle, Loader2, Sparkles, Lightbulb } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

interface Message {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  queryResponse?: {
    sql: string;
    columns: string[];
    allColumns?: string[];
    defaultVisibleColumns?: string[];
    columnAliases?: Record<string, string>;
    currencyColumns?: string[];
    results: any[];
    rowCount: number;
    totalRowCount?: number;
    isLimited?: boolean;
    explanation?: string;
    suggestedFollowUps?: string[];
    chartSuggestion?: ChartSuggestion;
    insights?: InsightResult;
  };
  isUpgradePrompt?: boolean;
  upgradeDetails?: { limit: number; phone: string };
}

const QueryPage: React.FC = () => {
  const { isConfigured } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [conversationId, setConversationId] = useState<string>(uuidv4());
  const [messages, setMessages] = useState<Message[]>([]);
  const [lastUserQuery, setLastUserQuery] = useState<string>(""); // Track the last user query
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle conversation continuation from navigation state
  useEffect(() => {
    const state = location.state as any;
    if (state?.newConversation) {
      handleNewConversation();
      window.history.replaceState(null, '');
    } else if (state?.continueConversation && state?.conversationId && state?.messages) {
      setConversationId(state.conversationId);
      setMessages(state.messages);
      window.history.replaceState(null, '');
    }
  }, [location.state]);

  const { mutate: executeQuery, isLoading } = useMutation(
    async (data: { query: string; conversationId?: string }) => {
      const hasMessages = messages.some(
        (m) => m.type === "assistant" && m.queryResponse
      );
      if (hasMessages) {
        return queryAPI.continueConversation(conversationId, data.query);
      } else {
        return queryAPI.processNaturalLanguage(data.query, conversationId);
      }
    },
    {
      onSuccess: (data: QueryResponse) => {
        if (data.type === 'conversational') {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              type: "assistant",
              content: data.message || 'I could not generate a response.',
              timestamp: new Date(),
            },
          ]);
          setQuery("");
          queryClient.invalidateQueries(['conversations']);
          return;
        }

        setMessages((prev) => [
          ...prev,
          {
            id: uuidv4(),
            type: "assistant",
            content: data.explanation || `Found ${data.rowCount} results`,
            timestamp: new Date(),
            queryResponse: {
              sql: data.query,
              columns: data.columns,
              allColumns: data.allColumns,
              defaultVisibleColumns: data.defaultVisibleColumns,
              columnAliases: data.columnAliases,
              currencyColumns: data.currencyColumns,
              results: data.results,
              rowCount: data.rowCount,
              totalRowCount: data.totalRowCount,
              isLimited: data.isLimited,
              explanation: data.explanation,
              suggestedFollowUps: data.suggestedFollowUps,
              chartSuggestion: data.chartSuggestion,
              insights: data.insights,
            },
          },
        ]);
        setQuery("");

        // Invalidate conversation queries to refresh the chat history
        queryClient.invalidateQueries(['conversations']);
      },
      onError: (error: any) => {
        const errorData = error.response?.data?.error;
        if (errorData?.code === 'CONVERSATION_LIMIT_EXCEEDED') {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              type: "assistant",
              content: "",
              timestamp: new Date(),
              isUpgradePrompt: true,
              upgradeDetails: {
                limit: errorData.details?.limit || 10,
                phone: errorData.details?.upgradePhone || '+1 (866) 658-2220',
              },
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: uuidv4(),
              type: "assistant",
              content: `Error: ${error.message || "Failed to process query"}`,
              timestamp: new Date(),
            },
          ]);
        }
      },
    }
  );

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (query.trim() && !isLoading) {
      const userMessage: Message = {
        id: uuidv4(),
        type: "user",
        content: query,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setLastUserQuery(query); // Save the query for CSV export
      executeQuery({ query, conversationId });
    }
  };

  const handleFollowUpClick = (question: string) => {
    setQuery(question);
    setLastUserQuery(question); // Save the follow-up query for CSV export
    // Auto-submit after a brief delay to show the question in the input
    setTimeout(() => {
      const userMessage: Message = {
        id: uuidv4(),
        type: "user",
        content: question,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      executeQuery({ query: question, conversationId });
      setQuery("");
    }, 100);
  };

  const handleNewConversation = () => {
    setConversationId(uuidv4());
    setMessages([]);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isConfigured) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please configure your subdomain in Settings to use the query
            interface.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const suggestions = [
    "What were my total sales last month?",
    "Show me top 10 customers by revenue",
    "What products are low in stock?",
    "Compare this year's performance to last year",
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 pt-6">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Sparkles className="h-16 w-16 text-brand mb-6" />
            <h1 className="text-[28px] font-semibold text-text-secondary mb-3">
              Meet Octo AI
            </h1>
            <p className="text-base text-text-tertiary max-w-lg text-center mb-8">
              Ask questions about your data in natural language and get instant
              insights powered by AI.
            </p>
            <div className="w-full max-w-lg">
              <p className="text-xs font-medium text-text-tertiary mb-3 flex items-center gap-1.5">
                <Lightbulb className="h-3.5 w-3.5" />
                Suggestions
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => handleFollowUpClick(suggestion)}
                    className="rounded-full bg-brand-subtle text-brand text-sm px-4 py-1.5 hover:bg-brand-stroke transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                onFollowUpClick={handleFollowUpClick}
                originalQuery={
                  message.type === "assistant" && message.queryResponse
                    ? lastUserQuery
                    : undefined
                }
                conversationId={conversationId}
              />
            ))}
            {isLoading && (
              <div className="flex gap-3 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  </div>
                </div>
                <Card className="animate-pulse">
                  <CardContent className="py-3 px-4">
                    <LoadingMessages />
                  </CardContent>
                </Card>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="px-6 pb-4 pt-2">
        <form
          onSubmit={handleSubmit}
          className="flex items-end gap-2 rounded-xl p-3"
          style={{
            boxShadow:
              "0px 1px 2px rgba(27,32,41,0.12), 0px 0px 0px 1px rgba(27,32,41,0.08)",
          }}
        >
          <Textarea
            ref={textareaRef}
            placeholder="Ask a question about your data..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="min-h-[40px] max-h-[200px] resize-none border-0 shadow-none focus-visible:ring-0 p-0"
            rows={1}
          />
          <button
            type="submit"
            disabled={isLoading || !query.trim()}
            className={cn(
              "flex-shrink-0 rounded-full w-9 h-9 flex items-center justify-center transition-colors",
              query.trim() && !isLoading
                ? "bg-brand text-white"
                : "bg-disabled-bg text-text-tertiary"
            )}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </form>
        <p className="text-xs text-text-tertiary text-center mt-2">
          Answers may contain inaccurate data
        </p>
      </div>
    </div>
  );
};

export default QueryPage;
